import { Table, Spin, Pagination } from '@/components/ui'
import { Link } from '@inertiajs/react'
import { EnvironmentOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { Event, Category } from '@/types/events'
import { EventOwner } from '@/pages/admin/event_owners/index.page'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { CategoryBadgeGroup } from './CategoryBadgeGroup'
import { OwnerAvatarGroup } from './OwnerAvatarGroup'
import { EventActionsCell } from './EventActionsCell'

interface EventsTableProps {
  events: Event[]
  isLoading: boolean
  isError: boolean
  error?: Error
  viewMode: 'list' | 'report'
  categories: Category[]
  owners: EventOwner[]
  onEdit?: (event: Event) => void
  onDelete?: (event: Event) => void
  onView?: (event: Event) => void
  onOwnerClick?: (event: Event) => void
}

export function EventsTable({
  events,
  isLoading,
  isError,
  error,
  viewMode,
  categories,
  owners,
  onEdit,
  onDelete,
  onView,
  onOwnerClick,
}: EventsTableProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page)
    setPageSize(size)
  }

  // Calculate paginated events
  const startIndex = (currentPage - 1) * pageSize
  const paginatedEvents = events.slice(startIndex, startIndex + pageSize)

  const columns = [
    {
      title: 'Event Name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      sorter: (a: Event, b: Event) => a.name.localeCompare(b.name),
      render: (name: string, record: Event) => (
        <div className="flex items-center gap-4">
          {record.logo ? (
            <img
              src={record.logo}
              alt={name}
              className="w-6 h-6 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0">
              <EnvironmentOutlined className="text-white text-xs" />
            </div>
          )}
          <Link
            href={viewMode === 'report' ? `/admin/event_reports/${record.id}` : `/admin/events/${record.id}`}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            {name}
          </Link>
        </div>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      width: '15%',
      sorter: (a: Event, b: Event) => (a.slug || '').localeCompare(b.slug || ''),
      render: (slug: string) => (
        <span className="text-xs text-gray-500 font-mono">{slug}</span>
      ),
    },
    {
      title: 'Registered',
      dataIndex: 'registered',
      key: 'registered',
      width: '10%',
      sorter: (a: Event, b: Event) => (a.registered || 0) - (b.registered || 0),
    },
    ...(viewMode === 'list'
      ? [
          // {
          //   title: 'Pending Approval',
          //   dataIndex: 'pending_approval',
          //   key: 'pending_approval',
          //   width: '12%',
          //   sorter: (a: Event, b: Event) => (a.pending_approval || 0) - (b.pending_approval || 0),
          // },
          // {
          //   title: 'Approved',
          //   dataIndex: 'approved',
          //   key: 'approved',
          //   width: '10%',
          //   sorter: (a: Event, b: Event) => (a.approved || 0) - (b.approved || 0),
          // },
        ]
      : []),
    // Conditional "Attended" column for report view
    ...(viewMode === 'report'
      ? [
          {
            title: 'Attended',
            dataIndex: 'attended',
            key: 'attended',
            width: '10%',
            sorter: (a: Event, b: Event) => (a.attended || 0) - (b.attended || 0),
          },
        ]
      : []),
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      width: '12%',
      sorter: (a: Event, b: Event) =>
        new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      width: '12%',
      sorter: (a: Event, b: Event) =>
        new Date(a.end_date).getTime() - new Date(b.end_date).getTime(),
    },
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      width: '15%',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (status: string) => <StatusBadge status={status as any} />,
      filters: [
        { text: 'Live', value: 'live' },
        { text: 'Draft', value: 'draft' },
        { text: 'Archived', value: 'archived' },
        { text: 'Purged', value: 'purged' },
      ],
      onFilter: (value: any, record: Event) => record.status === value,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: '10%',
      render: (_: any, record: Event) => {
        // Map category_ids to actual category objects
        const recordCategories =
          (record.category_ids
            ?.map((id) => categories.find((cat) => cat.id === id))
            .filter(Boolean) as Category[]) ||
          record.categories ||
          []

        return <CategoryBadgeGroup categories={recordCategories} />
      },
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: '10%',
      render: (_: any, record: Event) => {
        // Map owner_ids to actual owner objects
        const recordOwners =
          (record.owner_ids
            ?.map((id) =>
              owners.find((owner) => owner.id?.toString() === id.toString())
            )
            .filter(Boolean) as EventOwner[]) ||
          record.owners ||
          []

        return (
          <OwnerAvatarGroup
            owners={recordOwners}
            onClick={() => onOwnerClick?.(record)}
            editable={viewMode === 'list'}
          />
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Event) => (
        <EventActionsCell
          event={record}
          viewMode={viewMode}
          onEdit={onEdit}
          onDelete={onDelete}
          onView={onView}
        />
      ),
    },
  ]

  if (isLoading) {
    return (
      <div className="p-6 bg-[#f5f5f5]">
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-6 bg-[#f5f5f5]">
        <div className="text-red-500 text-center py-4">
          Error loading events: {error?.message}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 bg-[#f5f5f5]">
      <Table
        columns={columns}
        dataSource={paginatedEvents}
        rowKey="id"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
        }}
        pagination={false}
        className="ava-data-table"
        rowClassName="bg-transparent"
        style={{ backgroundColor: 'transparent' }}
      />
      {events.length > 0 && (
        <div className="mt-4">
          <Pagination
            current={currentPage}
            total={events.length}
            pageSize={pageSize}
            onChange={handlePaginationChange}
            showSizeChanger
            showQuickJumper
          />
        </div>
      )}
    </div>
  )
}
